import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import * as Sentry from '@sentry/capacitor';
import type { Breadcrumb, Event } from '@sentry/capacitor';
import * as SentryReact from '@sentry/react';
import Secrets from '@yumi/secrets';

interface MonitoringConfig {
  dsn?: string;
  tracesSampleRate?: string | number;
  environment?: string;
  pdaSupport?: string | boolean;
  platform?: string;
  release?: string;
  dist?: string | number;
}

type UnknownRecord = Record<string, unknown>;

const REDACTED = '[REDACTED]';
const MAX_SANITIZE_DEPTH = 5;
const SENSITIVE_KEYS = new Set([
  'accesstoken',
  'apikey',
  'auth',
  'authorization',
  'bearer',
  'cfaccessclientid',
  'cfaccessclientsecret',
  'cookie',
  'email',
  'firstname',
  'idtoken',
  'lastname',
  'password',
  'phone',
  'primarysid',
  'profile',
  'refreshtoken',
  'rut',
  'secret',
  'setcookie',
  'token',
  'user',
  'userid',
  'userinfo',
  'username',
  'userprofile',
]);
const SENSITIVE_SUFFIXES = ['authorization', 'cookie', 'password', 'secret', 'token'];

const normalizeKey = (value: string): string => value.replace(/[^a-z0-9]/gi, '').toLowerCase();
const isSensitiveKey = (value: string): boolean => {
  const normalized = normalizeKey(value);
  return SENSITIVE_KEYS.has(normalized) || SENSITIVE_SUFFIXES.some((suffix) => normalized.endsWith(suffix));
};

const sanitizeString = (value: string): string =>
  value
    .replace(/\bhttps?:\/\/[^@\s/]+@/gi, `https://${REDACTED}@`)
    .replace(/\bBearer\s+[^\s,;]+/gi, `Bearer ${REDACTED}`)
    .replace(/([?&](?:access_token|refresh_token|id_token|token|password|secret)=)[^&#\s]*/gi, `$1${REDACTED}`)
    .replace(/\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g, REDACTED)
    .replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, REDACTED)
    .replace(/\b\d{1,2}\.?\d{3}\.?\d{3}-?[\dkK]\b/g, REDACTED);

const stripQueryAndHash = (url: string): string => sanitizeString(url.split(/[?#]/)[0]);

const sanitizeValue = (input: unknown, key?: string, depth = 0, seen: WeakSet<object> = new WeakSet<object>()): unknown => {
  if (key && isSensitiveKey(key)) return REDACTED;
  if (typeof input === 'string') return sanitizeString(input);
  if (input === null || typeof input !== 'object') return input;
  if (input instanceof Date || input instanceof RegExp) return String(input);
  if (depth >= MAX_SANITIZE_DEPTH) return '[Filtered]';
  if (seen.has(input)) return '[Circular]';
  seen.add(input);

  try {
    if (Array.isArray(input)) {
      return input.map((item) => sanitizeValue(item, undefined, depth + 1, seen));
    }

    const sanitized: UnknownRecord = {};
    Object.entries(input as UnknownRecord).forEach(([entryKey, entryValue]) => {
      sanitized[entryKey] = sanitizeValue(entryValue, entryKey, depth + 1, seen);
    });
    return sanitized;
  } finally {
    seen.delete(input);
  }
};

const sanitizeUrl = (value?: string): string | undefined => (value ? stripQueryAndHash(value) : undefined);

const sanitizeSpanDescription = (description: string): string =>
  sanitizeString(stripQueryAndHash(description));

type SentrySpan = NonNullable<Event['spans']>[number];
type SentrySpanWithTags = SentrySpan & { tags?: Record<string, unknown> };

const sanitizeSpan = (span: SentrySpan): SentrySpan => {
  const spanWithTags: SentrySpanWithTags = span;
  const sanitized: SentrySpanWithTags = {
    ...spanWithTags,
    description:
      span.description && typeof span.description === 'string'
        ? sanitizeSpanDescription(span.description)
        : span.description,
  };

  if (span.data !== undefined) sanitized.data = sanitizeValue(span.data, 'span-data') as SentrySpan['data'];
  if (spanWithTags.tags !== undefined) {
    sanitized.tags = sanitizeValue(spanWithTags.tags, 'span-tags') as Record<string, unknown>;
  }

  return sanitized;
};

export const parseSentrySampleRate = (value?: string | number | null): number => {
  if (value === undefined || value === null || String(value).trim() === '') return 0.0;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || Number.isNaN(parsed)) return 0.0;
  return Math.min(1, Math.max(0, parsed));
};

export const normalizePlatform = (platform?: string | null): string => (platform || 'web').trim().toLowerCase();

export const getVariantLabel = (platform: string, pdaSupport: string | boolean): string => {
  const normalizedPlatform = normalizePlatform(platform);
  if (normalizedPlatform === 'android') {
    return String(pdaSupport).toLowerCase() === 'true' ? 'pda' : 'android';
  }
  return normalizedPlatform;
};

export const sanitizeHeaders = (headers?: Record<string, string>): Record<string, string> | undefined => {
  if (!headers) return undefined;

  return Object.entries(headers).reduce<Record<string, string>>((sanitized, [key, value]) => {
    if (!isSensitiveKey(key)) sanitized[key] = sanitizeString(String(value)).replace(/\s+/g, ' ').trim();
    return sanitized;
  }, {});
};

export const sanitizeBreadcrumbForSentry = (breadcrumb: Breadcrumb): Breadcrumb => {
  if (!breadcrumb.data) return { ...breadcrumb, message: breadcrumb.message ? sanitizeString(breadcrumb.message) : undefined };

  const data: UnknownRecord = { ...breadcrumb.data };
  if (typeof data.url === 'string') data.url = stripQueryAndHash(data.url);
  if (data.headers && typeof data.headers === 'object') {
    data.headers = sanitizeHeaders(data.headers as Record<string, string>);
  }

  ['body', 'query_string', 'request', 'request_body', 'response', 'response_body'].forEach((key) => {
    delete data[key];
  });

  return {
    ...breadcrumb,
    message: breadcrumb.message ? sanitizeString(breadcrumb.message) : undefined,
    data: sanitizeValue(data) as Record<string, unknown>,
  };
};

export const sanitizeEventForSentry = <T extends Event>(event: T): T => {
  const sanitized = {
    ...event,
    message: event.message ? sanitizeString(event.message) : undefined,
    transaction: sanitizeUrl(event.transaction),
    user: undefined,
  } as T;

  if (event.request) {
    sanitized.request = {
      ...event.request,
      url: event.request.url ? stripQueryAndHash(event.request.url) : undefined,
      headers: sanitizeHeaders(event.request.headers),
      query_string: undefined,
      cookies: undefined,
      data: undefined,
    };
  }

  if (event.breadcrumbs) sanitized.breadcrumbs = event.breadcrumbs.map(sanitizeBreadcrumbForSentry);
  if (event.extra) sanitized.extra = sanitizeValue(event.extra) as Event['extra'];
  if (event.contexts) sanitized.contexts = sanitizeValue(event.contexts) as Event['contexts'];
  if (event.exception?.values) {
    sanitized.exception = {
      ...event.exception,
      values: event.exception.values.map((exception) => ({
        ...exception,
        value: exception.value ? sanitizeString(exception.value) : exception.value,
      })),
    };
  }

  return sanitized;
};

export const sanitizeTransactionForSentry = <T extends Event>(event: T): T => {
  const sanitized = sanitizeEventForSentry(event);

  if (Array.isArray(event.spans)) {
    sanitized.spans = event.spans.map(sanitizeSpan);
  }

  return sanitized;
};

const addSafeBreadcrumb = (breadcrumb: Breadcrumb): void => {
  try {
    Sentry.addBreadcrumb(breadcrumb);
  } catch {
    // Monitoring must never affect application startup.
  }
};

const registerStartupContext = (platform: string, variant: string): void => {
  addSafeBreadcrumb({ category: 'app.startup', level: 'info', message: 'javascript.bootstrap' });

  try {
    Sentry.setTags({
      'app.platform': platform,
      'app.variant': variant,
    });
  } catch {
    // Monitoring must never affect application startup.
  }

  void App.addListener('appStateChange', ({ isActive }) => {
    addSafeBreadcrumb({
      category: 'app.lifecycle',
      level: 'info',
      message: isActive ? 'foreground' : 'background',
      data: { isActive },
    });
  }).catch(() => {
    addSafeBreadcrumb({ category: 'monitoring', level: 'warning', message: 'app-state-listener-unavailable' });
  });

  void App.getInfo()
    .then((appInfo) => {
      Sentry.setContext('application', {
        build: appInfo.build,
        id: appInfo.id,
        name: appInfo.name,
        platform,
        variant,
        version: appInfo.version,
      });
      Sentry.setTags({
        'app.build': appInfo.build,
        'app.version': appInfo.version,
      });
    })
    .catch(() => {
      addSafeBreadcrumb({ category: 'monitoring', level: 'warning', message: 'app-info-unavailable' });
    });

  void Device.getInfo()
    .then((deviceInfo) => {
      Sentry.setContext('device.runtime', {
        manufacturer: deviceInfo.manufacturer,
        model: deviceInfo.model,
        operatingSystem: deviceInfo.operatingSystem,
        osVersion: deviceInfo.osVersion,
        platform: deviceInfo.platform,
      });
      Sentry.setTags({
        'device.manufacturer': deviceInfo.manufacturer,
        'device.model': deviceInfo.model,
        'device.os_version': deviceInfo.osVersion,
      });
    })
    .catch(() => {
      addSafeBreadcrumb({ category: 'monitoring', level: 'warning', message: 'device-info-unavailable' });
    });
};

export const initializeMonitoring = (config: MonitoringConfig = {}): void => {
  const monitoringSecrets = Secrets as typeof Secrets & {
    REACT_APP_SENTRY_RELEASE?: string;
    REACT_APP_SENTRY_DIST?: string;
  };
  const dsn = (config.dsn ?? monitoringSecrets.REACT_APP_SENTRY_URL ?? '').trim();
  if (!dsn) return;

  const platform = normalizePlatform(config.platform ?? Capacitor.getPlatform());
  const pdaSupport = config.pdaSupport ?? monitoringSecrets.REACT_APP_PDA_SUPPORT ?? 'false';
  const variant = getVariantLabel(platform, pdaSupport);
  const environment = (config.environment ?? monitoringSecrets.REACT_APP_ENVIRONMENT ?? '').trim().toLowerCase() || undefined;
  const release = (config.release ?? monitoringSecrets.REACT_APP_SENTRY_RELEASE ?? '').trim() || undefined;
  const dist = String(config.dist ?? monitoringSecrets.REACT_APP_SENTRY_DIST ?? '').trim() || undefined;

  try {
    Sentry.init(
      {
        dsn,
        environment,
        release,
        dist,
        tracesSampleRate: parseSentrySampleRate(config.tracesSampleRate ?? monitoringSecrets.REACT_APP_SENTRY_TRACES_SAMPLE_RATE),
        integrations: [Sentry.browserTracingIntegration()],
        attachStacktrace: true,
        enableAutoSessionTracking: true,
        enableCaptureFailedRequests: false,
        enableNative: true,
        enableNativeCrashHandling: true,
        maxBreadcrumbs: 100,
        sendDefaultPii: false,
        // Native crash events do not pass through this JavaScript hook; the Android manifest keeps
        // send-default-pii=false and the SDK handles the pre-JS event path independently.
        beforeBreadcrumb: sanitizeBreadcrumbForSentry,
        beforeSend: sanitizeEventForSentry,
        beforeSendTransaction: sanitizeTransactionForSentry,
      },
      SentryReact.init,
    );

    registerStartupContext(platform, variant);
  } catch {
    // Monitoring configuration must never prevent application startup.
  }
};
